<template lang="pug">
CardMinimal(title="Card payments", :tooltip="$t('settings.card_payments_description')", tooltipClass="bottom-right").payout-account-details
  template(slot="header")
    .p-a-4
      .stripe
        .stripe-logo
          img(src="@/assets/stripe-logo.svg")
        .stripe-description
          p {{ $t('settings.using_stripe_part_1') }}&nbsp;
            a(href="https://stripe.com/customers") Stripe
            | &nbsp;{{ $t('settings.using_stripe_part_2') }}
          .m-y-2(v-if="accountCountry === 'se' && accountStatusState.state !== 'verified'")
            p {{ $t('settings.registration_proof_part_1') }}&nbsp;
              a(href="https://bolagsverket.se/be/sok/registreringsbevis/bestall-1.4299" target="_blank").link.link-bold {{ $t('settings.registration_proof_link_text') }}
            p.m-t-1 {{ $t('settings.registration_proof_part_2') }}
    .stripe-connector
      //.d-f.ai-c.fd-c.p-a-4(v-if="accountStatusState.state === 'incomplete'")
      .d-f.ai-c.fd-c.p-a-4(v-if="!hasStripe")
        Button(@click="createStripeAccount", :icon="stripeCreateState === 'creating' ? 'loading mdi-spin' : null").very-blue.m-t-2 {{ $t('settings.setup_stripe_account') }}
        p.m-t-2 {{ $t('settings.already_have_stripe_account') }}?&nbsp;
          a(@click="connectStripeAccount").link {{ $t('settings.click_here_to_connect') }}
      template(v-else)
        template(v-if="connectedStripeAccount")
          .p-a-4.stripe-connected: p {{ $t('settings.update_connected_account') }}&nbsp;
            a(href="https://dashboard.stripe.com") https://dashboard.stripe.com
            | .
        template(v-else)
          .p-a-4.d-f.ai-c.fd-c
            Button.very-font(@click="updateStripeAccount", :class="{'warning': accountStatusState.type === 'warning', 'critical': accountStatusState.type === 'fatal', 'very-blue': accountStatusState.type === 'success'}") {{ $t('settings.update_details') }}
  .bank-accounts(v-if="bankAccounts && bankAccounts.length > 0")
    ExpandableListItem(v-for="account in bankAccounts", :key="account.bank_account_id")
      .p-a-4.d-f.ai-c
        p {{ account.name }}
        Tag(:text="$t('settings.bank_default').toUpperCase()", icon="check", v-if="account.default").ml-a
      template(slot="dots", v-if="!account.default")
        ul
          li.blue(:class="`${account.default ? 'disabled' : ''}`", @click="account.default ? null : setAsDefault(account.bank_account_id)")
            i.mdi.mdi-home-outline
            | {{ $t('settings.bank_make_default') }}
          li.red(@click="removeAccount(account.bank_account_id)")
            i.mdi.mdi-trash-can-outline
            | {{ $t('products.remove') }}
  .bank-accounts(v-else-if="bankAccounts && bankAccounts.length === 0")
    .p-a-4: p.ta-c {{ $t('settings.bank_no_accounts') }}
  .bank-accounts.d-f.ai-c.jc-c.fd-c.ta-c(v-else).p-a-4
    i.mdi.mdi-loading.mdi-spin
  .p-a-4.d-f.ai-c.jc-c(slot="footerButton", @click="addBankAccount = true")
    i.mdi.mdi-plus-circle
    span {{ $t('settings.add_bank_account') }} for card payments
  portal(to="modals")
    Modal(:show="addBankAccount", :stuck="createState !== ''" @close="cancelAddBankAccount()" title="Add bank account for card payments").mid-height
      .p-a-4
        .d-f
          div.f-2
            p.m-b-1 {{ $t('common.currency') }} {{ newAccount.country }}
            FormDropdown(:items="currencies", :maxHeight="150" placeholder="Välj", :value="newAccount.currency", @input="currencies = $event.value").medium.m-r-2
          div.f-4
            p.m-b-1 {{ $t('common.country') }}
            FormDropdown(:items="countries", :maxHeight="150" placeholder="Välj", :value="newAccount.country", @input="countries = $event.value").medium
        template(v-if="newAccount.country !== ''")
          template(v-if="newAccount.country.toUpperCase() === 'US'")
            .d-f
              div.m-r-1
                p.m-b-1.m-t-2 {{ $t('settings.account') }}
                FormInput(type="text", :placeholder="ibanPlaceholder", v-model="newAccount.account_number", id="account-number", :outline="true").m-b-2
              div.m-l-1
                p.m-b-1.m-t-2 {{ $t('settings.routing') }}
                FormInput(type="text", :placeholder="ibanPlaceholder", v-model="newAccount.routing_number", id="account-routing", :outline="true").m-b-2
          template(v-else)
            p.m-b-1.m-t-2 IBAN
            FormInput(type="text", :placeholder="ibanPlaceholder", v-model="newAccount.iban", id="account-iban", :outline="true").m-b-2
      template(v-slot:footer)
        .d-f.f-w
          Button.m-r-4(@click="addBankValid ? createBankAccount() : null", style="white-space: nowrap", :disabled="!addBankValid", :class="`${addBankValid ? 'very-blue' : 'very-blue-light'}`")
            template(v-if="createState === ''") {{ $t('settings.add_bank_account') }}
            template(v-else-if="createState === 'creating'") {{ $t('settings.adding_bank_account') }}
            template(v-else-if="createState === 'created'") {{ $t('settings.bank_account_added') }}
          Button.ml-a.outline(@click="cancelAddBankAccount()") Cancel
</template>

<script>

import ExpandableListItem from '@/components/Lists/ExpandableListItem.vue';
import Tag from '@/components/tag.vue';

import { getQueryParameterValue } from '@/utils';

export default {
  components: {
    ExpandableListItem,
    Tag,
  },
  computed: {
    hasStripe() {
      return this.account.verification.enabled;
    },
    addBankValid() {
      const {
        country,
        currency,
        iban,
        account_number,
        routing_number,
      } = this.newAccount;

      if (country.toLowerCase() === 'us') {
        return currency !== '' && account_number !== '' && routing_number !== '';
      }

      return iban !== '' && currency !== '';
    },
    /**
     * Return id of account if assumed
     * @returns {Number}
     */
    assumedAccount() {
      return parseInt(this.$store.getters['User/assumed'], 10);
    },
    /**
     * Return a list of bank accounts
     * @returns {?Array}
     */
    bankAccounts() {
      return this.$store.getters['AccountManager/bankAccounts'];
    },
    /**
     * Return the current country the account has
     * @returns {String}
     */
    accountCountry() {
      return this.$store.getters['AccountManager/accountCountry'].toLowerCase();
    },
    /**
     * Return boolean if the stripe accoutn is a connected one or nor
     */
    connectedStripeAccount() {
      const { account } = this;
      return account && account.verification.account_type === 'standard';
    },
    /**
     * Get default currency
     * @returns {String}
     */
    defaultCurrency() {
      return this.$store.getters['AccountManager/defaultCurrency'];
    },
    /**
     * Get currencies
     */
    currencies: {
      /**
       * Getter
       * @returns {Array}
       */
      get() {
        const { currency } = this.newAccount;
        return this.$store.getters['AccountManager/currencies'].map(c => ({ value: c.value, string: c.string, selected: c.value === currency }));
      },
      /**
       * Setter
       * @param {String} value
       */
      set(value) {
        this.newAccount.country = '';
        this.newAccount.currency = value;
      },
    },
    /**
     * Get countries
     */
    countries: {
      /**
       * Getter
       * @returns {Array}
       */
      get() {
        const { currency } = this.newAccount;
        const countries = this.$store.getters['AccountManager/countries'][currency];

        if (!countries) {
          return [];
        }

        if (countries.length === 1) {
          // eslint-disable-next-line
          this.newAccount.country = countries[0].toLowerCase();
        }

        return countries.map((c) => {
          const accountCountry = this.accountCountries[this.$i18n.locale].find(ac => ac.value.toLowerCase() === c.toLowerCase());

          if (!accountCountry) {
            return null;
          }
          accountCountry.selected = accountCountry.value.toLowerCase() === this.newAccount.country.toLowerCase();
          return accountCountry;
        }).filter(c => c);
      },
      /**
       * Setter
       * @param {String} value
       */
      set(value) {
        this.newAccount.country = value;
      },
    },
    /**
     * Return iban placeholder for country
     * @returns {String}
     */
    ibanPlaceholder() {
      const { country } = this.newAccount;
      const ac = this.accountCountries[this.$i18n.locale].find(c => c.value.toLowerCase() === country.toLowerCase());
      return ac ? ac.ibanPlaceholder : '';
    },
  },
  watch: {
    /**
     * Watching for assumed. If changed, reload bank accounts
     */
    assumedAccount() {
      this.getBankAccounts();
    },
    /**
     * Watching for default currency and set currency directly
     */
    defaultCurrency: {
      immediate: true,
      /**
       * @param {String} currency
       */
      handler(currency) {
        this.currencies = currency;
      },
    },
  },
  methods: {
    /**
     * Get bank accounts from interwebs
     */
    getBankAccounts() {
      this.$store.dispatch('AccountManager/getBankAccounts');
    },
    /**
     * Remove a bank account
     * @param {String} id
     */
    async removeAccount(id) {
      const result = await this.$store.dispatch('AccountManager/removeBankAccount', { id });
      if (!result) {
        swal({ title: 'An error occured', text: 'For some reason we could not save this bankaccount information right now', icon: 'error' });
        return;
      }
      this.getBankAccounts();
    },
    /**
     * Set bank account as default
     * @param {String} bank_account_id
     */
    async setAsDefault(bank_account_id) {
      const result = await this.$store.dispatch('AccountManager/setDefaultBankAccount', { userId: this.user.id, bank_account_id });
      if (!result) {
        swal({ title: 'An error occured', text: 'For some reason we could not set this bankaccount as default right now', icon: 'error' });
        return;
      }
      this.getBankAccounts();
    },
    /**
     * Cancel create new bank account
     */
    cancelAddBankAccount() {
      this.addBankAccount = false;
      this.newAccount = {
        iban: '',
        country: '',
        currency: '',
        account_number: '',
        routing_number: '',
      };
      this.createState = '';
    },
    /**
     * Create a bankaccount with given details
     */
    async createBankAccount() {
      const {
        country,
        currency,
        account_number,
        routing_number,
        iban,
      } = this.newAccount;

      const stripe = Stripe(this.stripeKey);
      const payload = {
        country,
        currency,
        account_number: country.toLowerCase() === 'us' ? account_number : iban,
      };

      if (country.toLowerCase() === 'us') {
        payload.routing_number = routing_number;
      }

      this.createState = 'creating';

      const { token: stripeResponse, error: stripeError } = await stripe.createToken('bank_account', payload);
      if (stripeError) {
        this.createState = '';
        swal({ title: 'An error occured', text: 'For some reason we could not save this bankaccount information right now', icon: 'error' });
        return;
      }

      const bankAccount = await this.$store.dispatch('AccountManager/addBankAccount', { userId: this.user.id, token: stripeResponse.id });
      if (!bankAccount) {
        this.createState = '';
        swal({ title: 'An error occured', text: 'For some reason we could not save this bankaccount information right now', icon: 'error' });
        return;
      }

      this.createState = 'created';

      setTimeout(() => {
        this.createState = '';
        this.cancelAddBankAccount();
      }, 2000);

      this.getBankAccounts();
    },
    /**
     * Create a new stripeaccount with account country
     */
    async createStripeAccount() {
      const { accountCountry } = this;
      this.stripeCreateState = 'creating';
      const countrySet = await this.$store.dispatch('AccountManager/setLegalCountry', accountCountry);

      if (!countrySet) {
        swal({ title: 'An error occured', text: 'For some reason we could not do this right now. Please try again later.', icon: 'error' });
        this.stripeCreateState = '';
        return;
      }

      await this.$store.dispatch('AccountManager/getAccount');
      this.$nextTick(() => {
        this.stripeCreateState = '';
        this.updateStripeAccount();
      });
    },
    /**
     * Get connect-link to stripe and relocate user there
     */
    async connectStripeAccount() {
      const link = await this.$store.dispatch('AccountManager/getConnectLink');
      if (!link) {
        return;
      }

      window.open(link.url, '_self');
    },
    /**
     * Connect existing account from stripe with code from redirect Back to Dashboard
     * @param {String} code
     */
    async connectExistingAccount(code) {
      const connect = await this.$store.dispatch('AccountManager/connectStripeAccount', code);
      if (!connect) {
        swal({ title: 'An error occured', text: 'For some reason we could not set connect your Stripe account right now.', icon: 'error' });
        return;
      }

      await this.$store.dispatch('AccountManager/getAccount');
      this.$nextTick(() => {
        swal(`${this.$t('settings.stripe_account_connected')}!`, '', 'success');
      });
    },
    /**
     * Get update-link to stripe and relocate user there
     */
    async updateStripeAccount() {
      const { state } = this.accountStatusState;
      const link = await this.$store.dispatch(`AccountManager/${state === 'verified' ? 'onboardingUpdate' : 'onboardingVerification'}`);
      if (!link) {
        return;
      }

      window.open(link.url, '_self');
    },
  },
  data() {
    return {
      stripeKey: process.env.VUE_APP_STRIPEKEY,
      addBankAccount: false,
      stripeCreateState: '',
      createState: '',
      newAccount: {
        iban: '',
        country: '',
        currency: '',
        account_number: '',
        routing_number: '',
      },
    };
  },
  async mounted() {
    await this.$store.dispatch('AccountManager/getCountryCurrencies');
    this.getBankAccounts();

    const error = getQueryParameterValue('error');
    const code = getQueryParameterValue('code');

    if (error !== '') {
      swal({ title: 'An error occured', text: 'For some reason we could not set connect your Stripe account right now.', icon: 'error' });
      return;
    }

    if (code !== '') {
      this.connectExistingAccount(code);
    }
  },
};
</script>

<style lang="scss">
.payout-account-details {
  .modal-inner {
    @media (min-width: 650px) {
      min-width: 400px;
      max-width: 400px;
    }
  }
}
</style>

<style lang="scss" scoped>
.stripe-connector {
  border-top: 1px solid #E7E9EC;
}

.stripe {
  align-items: center;
  display: flex;

  .stripe-logo {
    min-width: 120px;
    margin-right: 10px;
    img {
      height: 44px;
      margin: 0 auto;
    }
  }
}
</style>
